import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore'
import type { Order } from '../../types'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'

const ORDERS_UPDATED_EVENT = 'wortelogy-orders-updated'

const ordersCacheByPlace: Record<string, Order[]> = {}
const counterCacheByPlace: Record<string, number> = {}
let ordersUnsubscribe: (() => void) | null = null

// Remove all undefined values recursively so Firestore setDoc never receives them.
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) =>
      v && typeof v === 'object' ? stripUndefined(v as any) : v,
    ) as unknown as T
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue
      if (Array.isArray(v) || (v && typeof v === 'object')) {
        result[key] = stripUndefined(v as any)
      } else {
        result[key] = v
      }
    }
    return result as T
  }
  return value
}

function ordersRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'orders')
}

function orderDoc(placeId: string, orderId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'orders', orderId)
}

function countersOrdersRef(placeId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'counters', 'orders')
}

/** Customer-app orders must never be removed by saveOrders' sync-delete when POS cache is briefly stale. */
function isCustomerAppOrderInFirestore(data: Record<string, unknown>): boolean {
  if (data.orderSource === 'customer_app') return true
  const key = data.customerTableKey
  return typeof key === 'string' && key.length > 0
}

export async function primeOrdersCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (ordersUnsubscribe) {
    ordersUnsubscribe()
    ordersUnsubscribe = null
  }
  const [ordersSnap, counterSnap] = await Promise.all([
    getDocs(ordersRef(placeId)),
    getDoc(countersOrdersRef(placeId)),
  ])
  ordersCacheByPlace[placeId] = ordersSnap.docs.map((d) => ({ id: d.id, ...d.data() } as Order))
  counterCacheByPlace[placeId] = (counterSnap.data()?.orderDisplayNumber as number) ?? 0
  ordersUnsubscribe = onSnapshot(ordersRef(placeId), (snapshot) => {
    ordersCacheByPlace[placeId] = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Order))
    window.dispatchEvent(new CustomEvent(ORDERS_UPDATED_EVENT))
  })
}

export function getOrders(): Order[] {
  const placeId = getCurrentPlaceId()
  return ordersCacheByPlace[placeId] ?? []
}

function persistCounter(): void {
  const placeId = getCurrentPlaceId()
  const value = counterCacheByPlace[placeId] ?? 0
  setDoc(countersOrdersRef(placeId), { orderDisplayNumber: value }).catch(
    (e) => console.error('Firestore counter write failed', e),
  )
}

export function getNextDisplayNumber(): string {
  const placeId = getCurrentPlaceId()
  const next = (counterCacheByPlace[placeId] ?? 0) + 1
  counterCacheByPlace[placeId] = next
  persistCounter()
  return String(next).padStart(3, '0')
}

export function saveOrders(orders: Order[]): void {
  const placeId = getCurrentPlaceId()
  ordersCacheByPlace[placeId] = orders
  window.dispatchEvent(new CustomEvent(ORDERS_UPDATED_EVENT))
  ;(async () => {
    const snap = await getDocs(ordersRef(placeId))
    const ids = new Set(orders.map((o) => o.id))
    for (const d of snap.docs) {
      if (ids.has(d.id)) continue
      const data = d.data() as Record<string, unknown>
      if (isCustomerAppOrderInFirestore(data)) {
        if (import.meta.env.DEV) {
          console.warn(
            '[saveOrders] Skipping sync-delete for customer-app order (not in POS cache):',
            d.id,
          )
        }
        continue
      }
      await deleteDoc(d.ref)
    }
    for (const order of orders) {
      const sanitized = stripUndefined(order)
      await setDoc(orderDoc(placeId, order.id), sanitized, { merge: true })
    }
  })().catch((e) => console.error('Firestore saveOrders failed', e))
}

export function addOrder(order: Order): Order[] {
  const placeId = getCurrentPlaceId()
  const cache = ordersCacheByPlace[placeId] ?? []
  const full: Order = stripUndefined(order)
  ordersCacheByPlace[placeId] = [...cache, full]
  setDoc(orderDoc(placeId, order.id), full).catch((e) =>
    console.error('Firestore addOrder failed', e),
  )
  window.dispatchEvent(new CustomEvent(ORDERS_UPDATED_EVENT))
  return ordersCacheByPlace[placeId]
}

export function updateOrder(id: string, patch: Partial<Order>): Order[] {
  const placeId = getCurrentPlaceId()
  const cache = ordersCacheByPlace[placeId] ?? []
  ordersCacheByPlace[placeId] = cache.map((o) => (o.id === id ? { ...o, ...patch } : o))
  const found = ordersCacheByPlace[placeId].find((o) => o.id === id)
  if (found) {
    const sanitizedPatch = stripUndefined(patch) as Record<string, unknown>
    if (Object.keys(sanitizedPatch).length > 0) {
      updateDoc(orderDoc(placeId, id), sanitizedPatch).catch((e) =>
        console.error('Firestore updateOrder failed', e),
      )
    }
  }
  window.dispatchEvent(new CustomEvent(ORDERS_UPDATED_EVENT))
  return ordersCacheByPlace[placeId]
}

export function deleteOrder(id: string): Order[] {
  const placeId = getCurrentPlaceId()
  const cache = ordersCacheByPlace[placeId] ?? []
  ordersCacheByPlace[placeId] = cache.filter((o) => o.id !== id)
  deleteDoc(orderDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteOrder failed', e),
  )
  window.dispatchEvent(new CustomEvent(ORDERS_UPDATED_EVENT))
  return ordersCacheByPlace[placeId]
}

export function isOrdersPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in ordersCacheByPlace
}

/** Clear in-memory caches and unsubscribe from real-time listener. */
export function clearOrdersCache(): void {
  if (ordersUnsubscribe) {
    ordersUnsubscribe()
    ordersUnsubscribe = null
  }
  Object.keys(ordersCacheByPlace).forEach((k) => delete ordersCacheByPlace[k])
  Object.keys(counterCacheByPlace).forEach((k) => delete counterCacheByPlace[k])
}
